import type React from 'react'
import {createContext, useCallback, use, useEffect, useMemo, useState} from 'react'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCalculatedMax} from './CalculatedMaxContext'
import {addUrlToHistoryStack} from '@github-ui/history'
import {getPreviousSaturday} from '../helpers/get-previous-saturday'

/**
 * Retrieves dates (as milliseconds since the epoch) from the `from` and `to` query parms.
 */
function readDateFromUrl(): {from?: number; to?: number} {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const fromParam = url.searchParams.get('from')
  const toParam = url.searchParams.get('to')
  const isAllParam = url.searchParams.has('all')
  const isLimitedDefaultRangeEnabled = isFeatureEnabled('repos_contributors_limited_default_range')

  let from = fromParam ? new Date(fromParam).getTime() : undefined
  const to = toParam ? new Date(toParam).getTime() : undefined

  if (isLimitedDefaultRangeEnabled && !isAllParam && !from && !to) {
    from = getPreviousSaturday({periodType: 'month', offset: 3})
  }

  return {from, to}
}

/**
 * Writes dates (formatted as YYYY-MM-DD strings) to the `from` and `to` query parms.
 */
function writeDateToUrl(date: {from?: number; to?: number}): void {
  const from = date.from ? new Date(date.from).toLocaleDateString() : undefined
  const to = date.to ? new Date(date.to).toLocaleDateString() : undefined
  const url = new URL(window.location.href, window.location.origin)
  const isLimitedDefaultRangeEnabled = isFeatureEnabled('repos_contributors_limited_default_range')

  if (isLimitedDefaultRangeEnabled && !from && !to) {
    url.searchParams.set('all', '1')
    url.searchParams.delete('from')
    url.searchParams.delete('to')
  } else {
    url.searchParams.delete('all')
    if (from) {
      url.searchParams.set('from', from)
    } else {
      url.searchParams.delete('from')
    }
    if (to) {
      url.searchParams.set('to', to)
    } else {
      url.searchParams.delete('to')
    }
  }
  addUrlToHistoryStack(url.toString())
}

export type RangeSelectionContextValues = {
  /** Earliest chart date, in milliseconds since the epoch */
  from?: number
  /** Latest chart date, in milliseconds since the epoch */
  to?: number
  /** Updates `from` and `to` in React state */
  setDate: (date: {from?: number; to?: number}) => void
}

type SetDateProps = Pick<RangeSelectionContextValues, 'from' | 'to'>

export const RangeSelectionContext = createContext<RangeSelectionContextValues>({} as RangeSelectionContextValues)

// This follows the “Extracting providers to a component” example in https://react.dev/reference/react/useContext#extracting-providers-to-a-component. This pattern keep context-specific state and effects in one place.
export function RangeSelectionProvider({children}: React.PropsWithChildren) {
  // Read dates from the URL on initial render
  const [{from, to}, setDate] = useState<SetDateProps>(() => readDateFromUrl())
  const {reset} = useCalculatedMax()

  const wrappedSetDate = useCallback(
    (args: SetDateProps) => {
      reset()
      setDate(args)
    },
    [reset, setDate],
  )

  // When dates change, update the URL
  useEffect(() => {
    writeDateToUrl({from, to})
  }, [from, to])

  const dateProviderValue = useMemo(
    () => ({
      from,
      to,
      setDate: wrappedSetDate,
    }),
    [from, to, wrappedSetDate],
  )

  return <RangeSelectionContext value={dateProviderValue}>{children}</RangeSelectionContext>
}

export function useRangeSelection() {
  return use(RangeSelectionContext)
}
